import { type FormEvent, useState } from "react";
import { ArrowLeft, ArrowRight, Lock } from "lucide-react";
import type { Answers, CatalogSchema, PickId } from "../../services/catalogApi";
import {
  type Details, FOLLOW_UPS, NUMBERS, type QuestionId, SETUP_HINTS, joinWords, matchProblems, monogram, toolChoices,
} from "./assessment";
import { Chip, OptionCard, primaryButton, textButton } from "./ui";

const TITLES: Partial<Record<QuestionId, { title: string; help?: string; intro?: string }>> = {
  problem: { title: "What eats up your team's time?", help: "Pick as many as you like, or tell us in your own words." },
  numbers: { title: "Which numbers do you check first each morning?", help: "Tap any that apply." },
  workspace: { title: "Where does your team do most of its work?",
               intro: "Now a few questions so we can fit this to how you already work." },
  data: { title: "How sensitive is the information you work with?", help: "This decides where your automations run." },
  payment: { title: "How would you like to pay to run it?", help: "No numbers yet. We'll go over pricing with you on a short call." },
  tools: { title: "Which tools does your team use every day?", help: "Tap all that apply. We'll connect them for you." },
  business: { title: "Who do you serve, and what field are you in?",
              help: "Businesses, public agencies, nonprofits, schools: every kind of organization is welcome." },
  team: { title: "How big is your team?", help: "Count everyone, including you." },
};

const toggle = (list: string[], value: string) => (list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

/** One question of the assessment. Single choices move on by themselves; the rest use Continue. */
export default function QuestionStep({ question, schema, answers, details, acknowledgement, runs, onChange, onContinue,
                                       onSkip, onBack }: {
  question: QuestionId;
  schema: CatalogSchema;
  answers: Answers;
  details: Details;
  acknowledgement: string;
  /** Where it will run, once the setup questions have worked it out. */
  runs: string | null;
  onChange: (answers: Answers, details: Details, advance?: boolean) => void;
  onContinue: () => void;
  onSkip: () => void;
  onBack: () => void;
}) {
  const [draft, setDraft] = useState("");
  const [heard, setHeard] = useState("");
  const [otherDraft, setOtherDraft] = useState("");
  const [industryDraft, setIndustryDraft] = useState("");
  // Industries the visitor typed in themselves, kept beside the catalog's own list.
  const ownIndustries = answers.industries.filter((i) => !schema.industries.includes(i));
  // Tool logos that failed to load; those tiles fall back to the tool's initials.
  const [brokenLogos, setBrokenLogos] = useState<Set<string>>(new Set());
  const followUp = question in FOLLOW_UPS ? FOLLOW_UPS[question as keyof typeof FOLLOW_UPS] : null;
  const pick = schema.picks.find((p) => p.id === question);
  const text = TITLES[question] ?? (followUp ? { title: followUp.title, help: followUp.help } : { title: pick?.question ?? "" });
  const title = pick?.question ?? text.title;

  const others = answers.other_tools ?? [];
  const addOtherTool = (event: FormEvent) => {
    event.preventDefault();
    const said = otherDraft.trim().slice(0, 60);
    if (!said || others.some((t) => t.toLowerCase() === said.toLowerCase())) return;
    onChange({ ...answers, other_tools: [...others, said].slice(0, 15) }, details);
    setOtherDraft("");
  };

  const addIndustry = (event: FormEvent) => {
    event.preventDefault();
    const said = industryDraft.trim().slice(0, 60);
    if (!said || answers.industries.some((i) => i.toLowerCase() === said.toLowerCase())) return;
    onChange({ ...answers, industries: [...answers.industries, said].slice(0, 10) }, details);
    setIndustryDraft("");
  };

  const addDraft = (event: FormEvent) => {
    event.preventDefault();
    const said = draft.trim();
    if (!said) return;
    const hits = matchProblems(said, schema.problems);
    if (hits.length) {
      onChange({ ...answers, problems: [...new Set([...answers.problems, ...hits])] }, details);
      setHeard(`We heard: ${joinWords(hits.map((h) => h.toLowerCase()))}. We picked ${hits.length > 1 ? "them" : "it"} below.`);
    } else {
      onChange(answers, { ...details, problemText: said });
      setHeard(`Thanks. We'll look for the closest fit for “${said.slice(0, 60)}”.`);
    }
    setDraft("");
  };

  const hasAnswer = {
    problem: answers.problems.length > 0 || !!details.problemText,
    signoff: details.signoff.length > 0,
    numbers: details.numbers.length > 0,
    tools: answers.tools.length > 0 || others.length > 0,
    business: !!answers.business_model || answers.industries.length > 0,
  }[question as string];
  const needsContinue = hasAnswer !== undefined;

  return (
    <div role="region" aria-labelledby="question-title"
             className="flex flex-col gap-5 rounded-2xl border border-borderSubtle bg-surface p-6 shadow-xl sm:p-9">
      {acknowledgement && (
        <p className="catalog-enter flex items-start gap-2.5 text-lg font-semibold text-primary">
          <span aria-hidden="true" className="mt-2.5 h-2 w-2 flex-none rounded-full bg-primary" />{acknowledgement}
        </p>
      )}
      {text.intro && <p className="self-start rounded-lg bg-primary/10 px-3 py-2 text-sm font-bold text-text-secondary">{text.intro}</p>}
      <div className="flex flex-col gap-1.5">
        <h2 id="question-title" className="font-display text-2xl font-bold text-text-primary sm:text-3xl">{title}</h2>
        {text.help && <p className="text-text-muted">{text.help}</p>}
      </div>

      {question === "problem" && (
        <div className="flex flex-col gap-5">
          <form onSubmit={addDraft} className="flex flex-col gap-2">
            <label htmlFor="problem-text" className="text-sm font-bold text-text-primary">In your own words</label>
            <div className="flex flex-wrap gap-2.5">
              <input id="problem-text" type="text" value={draft} maxLength={300} autoComplete="off"
                     onChange={(e) => setDraft(e.target.value)}
                     placeholder="For example: our bills arrive in five different inboxes"
                     className="min-h-[52px] min-w-0 flex-[999_1_260px] rounded-xl border border-borderSubtle bg-white px-4 text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/50" />
              <button type="submit" className="min-h-[52px] flex-auto rounded-xl border border-primary/40 bg-white px-5 font-bold text-primary hover:bg-primary/5">
                Add this
              </button>
            </div>
            {heard && <p key={heard} role="status" className="catalog-enter text-sm font-semibold text-primary">{heard}</p>}
          </form>
          <div className="flex flex-col gap-2.5">
            <span id="problem-chips" className="text-sm font-bold text-text-primary">Or tap any that sound familiar</span>
            <div role="group" aria-labelledby="problem-chips" className="catalog-stagger flex flex-wrap gap-2.5">
              {schema.problems.map((p) => (
                <Chip key={p} pressed={answers.problems.includes(p)}
                      onClick={() => onChange({ ...answers, problems: toggle(answers.problems, p) }, details)}>{p}</Chip>
              ))}
            </div>
          </div>
        </div>
      )}

      {followUp && (
        <div role="group" aria-labelledby="question-title" className="catalog-stagger grid gap-3 sm:grid-cols-2">
          {followUp.options.map(([value, label]) => {
            if (question === "signoff") {
              const next = value === "unsure"
                ? (details.signoff.includes("unsure") ? [] : ["unsure"])
                : toggle(details.signoff.filter((s) => s !== "unsure"), value);
              return <OptionCard key={value} multi label={label} pressed={details.signoff.includes(value)}
                                 onClick={() => onChange(answers, { ...details, signoff: next })} />;
            }
            const key = question as "freq" | "volume";
            return <OptionCard key={value} label={label} pressed={details[key] === value}
                               onClick={() => onChange(answers, { ...details, [key]: value }, true)} />;
          })}
        </div>
      )}

      {question === "numbers" && (
        <div role="group" aria-labelledby="question-title" className="catalog-stagger flex flex-wrap gap-2.5">
          {NUMBERS.map((n) => (
            <Chip key={n} pressed={details.numbers.includes(n)}
                  onClick={() => onChange(answers, { ...details, numbers: toggle(details.numbers, n) })}>{n}</Chip>
          ))}
        </div>
      )}

      {pick && (
        <div role="group" aria-labelledby="question-title" className="catalog-stagger grid gap-3 sm:grid-cols-2">
          {pick.options.map((o) => (
            <OptionCard key={o.id} label={o.label} hint={SETUP_HINTS[o.id]} pressed={answers.picks[pick.id] === o.id}
                        onClick={() => onChange({ ...answers, picks: { ...answers.picks, [pick.id as PickId]: o.id } }, details, true)} />
          ))}
        </div>
      )}

      {question === "tools" && (
        <div className="flex flex-col gap-5">
        <div role="group" aria-labelledby="question-title" className="catalog-stagger grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4">
          {toolChoices(schema).map(({ name, logo }) => {
            const pressed = answers.tools.includes(name);
            const mark = logo && !brokenLogos.has(name) ? logo : null;
            return (
              <button key={name} type="button" aria-pressed={pressed}
                      onClick={() => onChange({ ...answers, tools: toggle(answers.tools, name) }, details)}
                      className={`flex min-h-[60px] items-center gap-2.5 rounded-xl border-2 px-3 text-left transition-colors ${
                        pressed ? "border-primary bg-primary/10" : "border-borderSubtle bg-white hover:border-primary/50"}`}>
                <span aria-hidden="true" className={`flex h-9 w-9 flex-none items-center justify-center rounded-lg text-sm font-extrabold ${
                  pressed ? "bg-primary text-white" : "bg-slate-100 text-text-secondary"}`}>
                  {mark
                    ? <img src={mark} alt="" loading="lazy" className="h-6 w-6 object-contain"
                           onError={() => setBrokenLogos((s) => new Set(s).add(name))} />
                    : monogram(name)}
                </span>
                <span className="text-[15px] font-semibold leading-tight text-text-primary">{name}</span>
              </button>
            );
          })}
        </div>
          <form onSubmit={addOtherTool} className="flex flex-col gap-2">
            <label htmlFor="other-tool" className="text-sm font-bold text-text-primary">Anything else your team uses?</label>
            <p className="text-text-muted">Type any tool we haven't listed, and we'll note it down.</p>
            <div className="flex flex-wrap gap-2.5">
              <input id="other-tool" type="text" value={otherDraft} maxLength={60} autoComplete="off"
                     onChange={(e) => setOtherDraft(e.target.value)}
                     placeholder="e.g. Notion, Zendesk, your invoicing app"
                     className="min-h-[52px] min-w-0 flex-[999_1_260px] rounded-xl border border-borderSubtle bg-white px-4 text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/50" />
              <button type="submit" className="min-h-[52px] flex-auto rounded-xl border border-primary/40 bg-white px-5 font-bold text-primary hover:bg-primary/5">
                Add this
              </button>
            </div>
            {others.length > 0 && (
              <div role="group" aria-label="Other tools you named" className="flex flex-wrap gap-2.5 pt-1">
                {others.map((t) => (
                  <button key={t} type="button" aria-label={`Remove ${t}`}
                          onClick={() => onChange({ ...answers, other_tools: others.filter((x) => x !== t) }, details)}
                          className="flex min-h-[40px] items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-4 font-semibold text-text-primary">
                    {t}<span aria-hidden="true" className="text-text-muted">&times;</span>
                  </button>
                ))}
              </div>
            )}
          </form>
        </div>
      )}

      {question === "business" && (
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-2.5">
            <span className="text-sm font-bold text-text-primary">Who do you serve?</span>
            <div role="group" aria-label="Who do you serve?" className="flex flex-wrap gap-2.5">
              {schema.business_models.map((b) => (
                <Chip key={b.id} pressed={answers.business_model === b.id}
                      onClick={() => onChange({ ...answers, business_model: answers.business_model === b.id ? undefined : b.id }, details)}>
                  {b.label}
                </Chip>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-2.5">
            <span className="text-sm font-bold text-text-primary">What field are you in?</span>
            <div role="group" aria-label="What field are you in?" className="flex flex-wrap gap-2.5">
              {schema.industries.map((i) => (
                <Chip key={i} pressed={answers.industries.includes(i)}
                      onClick={() => onChange({ ...answers, industries: toggle(answers.industries, i) }, details)}>{i}</Chip>
              ))}
            </div>
          </div>
          <form onSubmit={addIndustry} className="flex flex-col gap-2">
            <label htmlFor="other-industry" className="text-sm font-bold text-text-primary">Not listed?</label>
            <p className="text-text-muted">Describe your field in your own words, and we'll note it down.</p>
            <div className="flex flex-wrap gap-2.5">
              <input id="other-industry" type="text" value={industryDraft} maxLength={60} autoComplete="off"
                     onChange={(e) => setIndustryDraft(e.target.value)}
                     placeholder="e.g. City government, a food bank, a dental practice"
                     className="min-h-[52px] min-w-0 flex-[999_1_260px] rounded-xl border border-borderSubtle bg-white px-4 text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/50" />
              <button type="submit" className="min-h-[52px] flex-auto rounded-xl border border-primary/40 bg-white px-5 font-bold text-primary hover:bg-primary/5">
                Add this
              </button>
            </div>
            {ownIndustries.length > 0 && (
              <div role="group" aria-label="Fields you named" className="flex flex-wrap gap-2.5 pt-1">
                {ownIndustries.map((i) => (
                  <button key={i} type="button" aria-label={`Remove ${i}`}
                          onClick={() => onChange({ ...answers, industries: answers.industries.filter((x) => x !== i) }, details)}
                          className="flex min-h-[40px] items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-4 font-semibold text-text-primary">
                    {i}<span aria-hidden="true" className="text-text-muted">&times;</span>
                  </button>
                ))}
              </div>
            )}
          </form>
        </div>
      )}

      {question === "team" && (
        <div role="group" aria-labelledby="question-title" className="catalog-stagger grid gap-3 sm:grid-cols-2">
          {schema.team_sizes.map((size) => (
            <OptionCard key={size} label={size} pressed={answers.team_size === size}
                        onClick={() => onChange({ ...answers, team_size: size }, details, true)} />
          ))}
        </div>
      )}

      {runs && pick && (
        <div role="status" className="catalog-enter flex items-start gap-3.5 rounded-2xl border border-primary/30 bg-primary/5 px-5 py-4">
          <span aria-hidden="true" className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-primary text-white">
            <Lock className="h-4 w-4" />
          </span>
          <div className="flex flex-col">
            <span className="text-sm font-extrabold text-text-primary">Where it will run</span>
            <span className="text-text-secondary">{runs}</span>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 border-t border-borderSubtle pt-5">
        <button type="button" onClick={onBack} className={textButton}><ArrowLeft className="h-4 w-4" aria-hidden="true" />Back</button>
        <span className="flex-1" />
        <button type="button" onClick={onSkip} className={textButton}>Skip</button>
        {needsContinue && (
          <button type="button" onClick={onContinue} disabled={!hasAnswer} className={primaryButton}>
            Continue<ArrowRight className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
}
